import React, { useState } from 'react';
import type { UploadFile } from 'antd/es/upload/interface';

import {
  trackUploadFileSuccess,
  trackUploadBatchSuccess,
  trackUploadError,
} from '../../../analytics';

import { SELECTS_UPLOAD_LIMIT, MAX_FILE_SIZE_MB } from '../../../constants';

import { updateEvent } from '../../../services/api/events';
import { uploadFile } from '../../../services/uploadFileService';

import { useProjectStore } from '../../../stores/project';
import { useUserStore } from '../../../stores/user';

import { FileKind, UploadingFile } from '../../../types';
import { isEmpty } from '../../../utils';

import AssetsUpload from './assetsUpload';
import ShotModal from './shotModal';
import UploadErrorModal from './uploadErrorModal';
import VideoModeModal from './videoModeModal';

interface Props {
  type: 'hl-reel' | 'clips';
  uploadRef: React.MutableRefObject<null>;
  handleDropAll?: (fileList: UploadFile[]) => void;
  onDrop: (file: UploadFile) => void;
  onProgress: (file: UploadingFile) => void;
  onComplete: (file: UploadFile) => void;
  onError: (file: UploadFile) => void;
  isSample: boolean;
}

const FileDropArea: React.FC<Props> = (props) => {
  const {
    type,
    uploadRef,
    handleDropAll,
    onDrop,
    onProgress,
    onComplete,
    onError,
    isSample,
  } = props;

  const { user } = useUserStore();
  const { eventId, currentProject, updateCurrentEvent } = useProjectStore();

  const [videoFiles, setVideoFiles] = useState<UploadFile[]>([]);

  const [showNoteModal, setShowNoteModal] = useState(false);
  const [noteModalContent, setNoteModalContent] = useState('');
  const [showModeModal, setShowModeModal] = useState(false);
  const [showShotModal, setShowShotModal] = useState(false);

  if (!currentProject) return;

  // TODO: Move this into a store when the backend is able to store config
  const videoModeSaved =
    JSON.parse(localStorage.getItem('videoModeSaved') ?? '{}')[eventId] ??
    false;

  // uploads each video in an array
  const handleUpload = async (newVideoFiles?: UploadFile[]) => {
    // use either custom video array or the one saved in state
    const videos = newVideoFiles || videoFiles;

    const analyticsPage = type.replace(/-/g, '_');

    if (!isEmpty(videos)) {
      let totalVideoDuration = 0;
      let oneTimeGA4Sent = false;

      videos.forEach((video) => {
        onDrop(video);

        // Create a video element to get the duration
        const videoElement = document.createElement('video');
        videoElement.preload = 'metadata';

        videoElement.onloadedmetadata = function () {
          window.URL.revokeObjectURL(videoElement.src);
          const duration = videoElement.duration;
          totalVideoDuration = totalVideoDuration + duration;

          const isPublic = currentProject.isAssigneeAccess ?? false;

          const fileKind: FileKind =
            type === 'hl-reel' ? 'video-template' : 'video-footage';

          // Proceed with file upload after getting the duration
          uploadFile({ file: video, eventId, fileKind, onProgress, isPublic })
            // when upload is complete – run actions from the parent
            .then(() => {
              // track each individual video upload with its duration
              trackUploadFileSuccess(analyticsPage, eventId, duration);

              // Clips custom GA4 event for total duration - One Time
              if (type === 'clips' && !oneTimeGA4Sent) {
                trackUploadBatchSuccess('clips', eventId, totalVideoDuration);
                oneTimeGA4Sent = true;
              }

              onComplete(video);
            })
            // if error happens – run actions from the parent
            .catch(() => {
              trackUploadError(analyticsPage, video.name);

              onError(video);
            });
        };

        videoElement.onerror = function () {
          // Handle error if the video cannot be loaded
          onError(video);
        };

        // Set the video src to the selected video file
        videoElement.src = URL.createObjectURL(
          (video.originFileObj || video) as File,
        );
      });

      if (type === 'hl-reel') {
        await updateEvent(eventId, { stage: 'upload' });
        await updateCurrentEvent();
      }
    }

    // clear video files after uploaded
    setVideoFiles([]);
  };

  const beforeUpload = (file: UploadFile, fileList: UploadFile[]) => {
    handleDropAll && handleDropAll(fileList);

    if (type === 'clips') {
      // check if number of files exceeds the maximum allowed number
      if (fileList.length > SELECTS_UPLOAD_LIMIT) {
        console.error('Maximum file count exceeded');
        setShowNoteModal(true);
        setNoteModalContent(
          `Maximum number of files exceeded! To optimise the uploading process, please only upload <b>${SELECTS_UPLOAD_LIMIT} files</b> at a time.`,
        );
        return false;
      }

      // check if file size exceeds the maximum allowed size
      if ((file.size ?? 0) > MAX_FILE_SIZE_MB * 1024 * 1024) {
        console.error('Maximum file size exceeded');
        setShowNoteModal(true);
        setNoteModalContent(
          `File size exceeds the maximum limit of <b>${MAX_FILE_SIZE_MB} MB</b>! To optimise the uploading process, Please upload a smaller file.`,
        );
        return false;
      }
    }

    // if video mode is NOT selected or the reminder is NOT saved – save file list in state and show mode modal
    if (!currentProject.videoMode || !videoModeSaved) {
      setVideoFiles(fileList);
      setShowModeModal(true);
    }

    // else if type is highlight reel and the reminder is NOT saved – save file list in state and show mode modal
    else if (type === 'hl-reel' && !user?.dismissedShotReminder) {
      setVideoFiles(fileList);
      setShowShotModal(true);
    }

    // else – handle upload one by one
    else handleUpload([file]);

    // file is never uploading via customRequest
    return false;
  };

  return (
    <React.Fragment>
      <AssetsUpload
        type={type}
        uploadRef={uploadRef}
        beforeUpload={beforeUpload}
        isSample={isSample}
      />

      <UploadErrorModal
        show={showNoteModal}
        onHide={() => setShowNoteModal(false)}
        noteModalContent={noteModalContent}
      />

      <VideoModeModal
        show={showModeModal}
        onHide={() => setShowModeModal(false)}
        type={type}
        handleUpload={handleUpload}
        rejectUpload={() => setVideoFiles([])}
        onShowShot={() => setShowShotModal(true)}
      />

      <ShotModal
        show={showShotModal}
        onHide={() => setShowShotModal(false)}
        mode={currentProject.videoMode}
        handleUpload={handleUpload}
        rejectUpload={() => setVideoFiles([])}
      />
    </React.Fragment>
  );
};

export default FileDropArea;
